import {NgModule} from "@angular/core";
import {CommonModule} from "@angular/common";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {RouterModule, Routes} from "@angular/router";
import {NgxChartsModule} from "@swimlane/ngx-charts";
import {NgxIntlTelInputModule} from "ngx-intl-tel-input";
import {CountdownModule} from 'ngx-countdown';
import {NgxPrintModule} from "ngx-print";

import {DndModule} from "ng2-dnd";
import {AccordionModule} from "primeng/accordion";
import {AutoCompleteModule} from "primeng/autocomplete";
import {BreadcrumbModule} from "primeng/breadcrumb";
import {ButtonModule} from "primeng/button";
import {CalendarModule} from "primeng/calendar";
import {CheckboxModule} from "primeng/checkbox";
import {ConfirmDialogModule} from "primeng/confirmdialog";
import {ContextMenuModule} from "primeng/contextmenu";
import {DialogModule} from "primeng/dialog";
import {DragDropModule} from "primeng/dragdrop";
import {DropdownModule} from "primeng/dropdown";
import {FieldsetModule} from "primeng/fieldset";
import {FileUploadModule} from "primeng/fileupload";
import {FullCalendarModule} from "primeng/fullcalendar";
import {InputMaskModule} from "primeng/inputmask";
import {InputSwitchModule} from "primeng/inputswitch";
import {InputTextareaModule} from "primeng/inputtextarea";
import {InplaceModule} from "primeng/inplace";
import {InputTextModule} from "primeng/inputtext";
import {KeyFilterModule} from "primeng/keyfilter";
import {MenubarModule} from "primeng/menubar";
import {MenuModule} from "primeng/menu";
import {MultiSelectModule} from "primeng/multiselect";
import {OverlayPanelModule} from "primeng/overlaypanel";
import {PanelModule} from "primeng/panel";
import {PanelMenuModule} from "primeng/panelmenu";
import {PasswordModule} from "primeng/password";
import {PickListModule} from "primeng/picklist";
import {RadioButtonModule} from "primeng/radiobutton";
import {RippleModule} from "primeng/ripple";
import {SelectButtonModule} from "primeng/selectbutton";
import {SpinnerModule} from "primeng/spinner";
import {StepsModule} from "primeng/steps";
import {TableModule} from "primeng/table";
import {TabViewModule} from "primeng/tabview";
import {ToastModule} from "primeng/toast";
import {ToggleButtonModule} from "primeng/togglebutton";
import {ToolbarModule} from "primeng/toolbar";
import {TooltipModule} from "primeng/tooltip";
import {TreeModule} from "primeng/tree";
import {TreeTableModule} from "primeng/treetable";

import {ConfirmationService} from "primeng/api";

import {KeycloakGuardService} from "@app/core";
import {Roles} from "@app/shared/enums/roles.enum";

// common re-usable pipe
import {AccountNumberMaskPipe} from "./pipes/account-number-mask-pipe.pipe";
import {BooleanPipe} from "./pipes/boolean.pipe";
import {NegativeCurrencyPipe} from "@app/shared/pipes/negative-currency.pipe";
import {PhonePipe} from "./pipes/phone.pipe";
import {RouteParamPipe} from "@app/shared/pipes/route-param.pipe";
import {SafeHtmlPipe} from "./pipes/safe-html.pipe";
import {SsnMaskPipe} from "./pipes/ssn-mask.pipe";
import {TitleCasePipe} from "./pipes/title-case.pipe";

// common re-usable services
import {AccountService} from "./services/account.service";
import {AddressService} from "@app/shared/services/address.service";
import {CalendarService} from "./services/calendar.service";
import {ChargeCodeService} from "./services/charge-code.service";
import {CommonDataService} from "./services/common-data.service";
import {CommonDocRepoService} from "./services/common-doc-repo.service";
import {CommonTransformationService} from "./services/common-transformation.service";
import {CommonTreeService} from "./services/common-tree.service";
import {DateService} from "./services/date.service";
import {DirectoryService} from "./services/directory.service";
import {FileService} from "./services/file.service";
import {HrService} from "./services/hr.service";
import {ItemCatalogService} from "./services/item-catalog.service";
import {LocationService} from "@app/shared/services/location.service";
import {MathService} from "./services/math.service";
import {PaymentService} from "@app/shared/services/payment.service";
import {PayrollService} from "@app/shared/services/payroll.service";
import {PhoneService} from "@app/shared/services/phone.service";
import {ProcurementService} from "./services/procurement.service";
import {ProjectService} from "./services/project.service";
import {RecruitingService} from "./services/recruiting.service";
import {RestrictedSolutionManagerService} from "./services/restricted-solution-manager.service";
import {TradingPartnerService} from "./services/trading-partner.service";
import {UtilityService} from "./services/utility.service";

// common re-usable components
import {AchFaqComponent} from "./ach-faq/ach-faq.component";
import {AccountAutoCompleteComponent} from "./account-auto-complete/account-auto-complete.component";
import {AccountsByRootAccountComponent} from "./accounts-by-root-account/accounts-by-root-account.component";
import {AchSelectFundingSourceComponent} from "./ach/ach-select-funding-source/ach-select-funding-source.component";
import {AddLineItemComponent} from "./item-catalog/add-line-item/add-line-item.component";
import {ApprovableMenuComponent} from "./approvable-menu/approvable-menu.component";
import {ApprovableStatusComponent} from "./approvable-status/approvable-status.component";
import {ApprovalStatusComponent} from "./approval-status/approval-status.component";
import {CalendarComponent} from "./calendar/calendar.component";
import {ChargeCodeAutoCompleteComponent} from "./charge-code/charge-code-auto-complete/charge-code-auto-complete.component";
import {CompletionEstimateComponent} from "./completion-estimate/completion-estimate.component";
import {ConnectComponent} from "./connect/connect.component";
import {ContractTreeComponent} from "./contract-tree/contract-tree.component";
import {DefaultChargeCodeComponent} from "./charge-code/default-charge-code/default-charge-code.component";
import {DraftComponent} from "./draft/draft.component";
import {EntityErrorComponent} from "./entity-error/entity-error.component";
import {ExcelFileLoadComponent} from '@app/shared/file/excel-file-load/excel-file-load.component';
import {FileExplorerComponent} from "@app/shared/file/file-explorer/file-explorer.component";
import {FileLoadComponent} from "@app/shared/file/file-load/file-load.component";
import {FilePermissionSettingComponent} from "@app/shared/file/file-permission-setting/file-permission-setting.component";
import {GrowlComponent} from "@app/shared/growl/growl.component";
import {InternalContactsComponent} from "./internal-contacts/internal-contacts.component";
import {ItemCatalogAddItemComponent} from "./item-catalog/item-catalog-add-item/item-catalog-add-item.component";
import {LeaveHistoryComponent} from "./leave-history/leave-history.component";
import {ManageTaxRateSetComponent} from "./org-admin/manage-tax-rate-set/manage-tax-rate-set.component";
import {ModHistoryComponent} from "./mod-history/mod-history.component";
import {MyChargeCodeDropdownComponent} from "./charge-code/my-charge-code-dropdown/my-charge-code-dropdown.component";
import {OrgAdditionalInfoComponent} from "./org-admin/org-additional-info/org-additional-info.component";
import {OrgAccountSettingComponent} from "./org-admin/org-account-setting/org-account-setting.component";
import {OrgContactComponent} from "./org-admin/org-contact/org-contact.component";
import {OrgGeneralInfoComponent} from "./org-admin/org-general-info/org-general-info.component";
import {OrgPaymentInfoComponent} from "./org-admin/org-payment-info/org-payment-info.component";
import {OrgSummaryComponent} from "./org-admin/org-summary/org-summary.component";
import {OrgTerminationComponent} from "./org-admin/org-termination/org-termination.component";
import {PaymentDialogComponent} from "./payment/payment-dialog/payment-dialog.component";
import {PaymentStatusComponent} from "./ach/payment-status/payment-status.component";
import {PdfTemplateDropdownComponent} from "./pdf-template-dropdown/pdf-template-dropdown.component";
import {ProjectResourceBalanceComponent} from "./project-resource-balance/project-resource-balance.component";
import {ProjectTaskBalanceComponent} from "./project-task-balance/project-task-balance.component";
import {ProjectTaskTreeComponent} from "./project-task-tree/project-task-tree.component";
import {RevenueBalanceComponent} from "./revenue-balance/revenue-balance.component";
import {ShowChargeCodeComponent} from "./charge-code/show-charge-code/show-charge-code.component";
import {SearchItemComponent} from "./item-catalog/search-item/search-item.component";
import {SubmittedForComponent} from "./submitted-for/submitted-for.component";
import {SupportChatComponent} from "./support-chat/support-chat.component";
import {TaxInformationComponent} from "./tax-information/tax-information.component";
import {TemplateComponent} from "./template/template.component";
import {TimerComponent} from "./timer/timer.component";
import {TodoComponent} from "./todo/todo.component";
import {TxnErrorCodeComponent} from "./txn-error-code/txn-error-code.component";
import {WorkflowStatusComponent} from "./workflow-status/workflow-status.component";
import {StringReplacePipe} from "@app/shared/pipes/string-replace.pipe";
import {RateSummaryComponent} from "@app/shared/rate-summary/rate-summary.component";
import {RateService} from "@app/shared/services/rate.service";
import {InternationalPhoneNumberComponent} from './international-phone-number/international-phone-number.component';
import {PrimeNgSupportService} from "@app/shared/services/prime-ng-support.service";
import {DataTableComponent} from './table/data-table/data-table.component';
import {VarDirective} from './directives/var.directive';
import {ContractInfoComponent} from './contract-info/contract-info.component';
import {ProjectInfoComponent} from './project-info/project-info.component';
import {DataTableRendererComponent} from './table/data-table-renderer/data-table-renderer.component';
import {ClinInfoComponent} from './clin-info/clin-info.component';
import {TreeDataTableComponent} from './table/tree-data-table/tree-data-table.component';
import {AbstractDataTableComponent} from './table/abstract-data-table/abstract-data-table.component';
import {DefaultCaptionComponent} from './table/default-caption/default-caption.component';
import {DataTableExportComponent} from './table/data-table-export/data-table-export.component';
import { AchUpdateCustomerComponent } from './ach/ach-update-customer/ach-update-customer.component';
import { AlternateWorkWeekComponent } from './alternate-work-week/alternate-work-week.component';
import { LegacyInvoiceComponent } from './legacy-invoice/legacy-invoice.component';
import { TransactionDataImportComponent } from './import/data-import/transaction-data-import.component';
import { InvoiceImportComponent } from './import/data-import/invoice-import/invoice-import.component';
import { ExpenseReportImportComponent } from './import/data-import/expense-report-import/expense-report-import.component';
import { InvoiceVendorMappingsComponent } from './import/data-import/invoice-import/invoice-vendor-mappings/invoice-vendor-mappings.component';
import { InvoiceChargeCodeMappingsComponent } from './import/data-import/invoice-import/invoice-charge-code-mappings/invoice-charge-code-mappings.component';
import { InvoiceItemMappingsComponent } from './import/data-import/invoice-import/invoice-item-mappings/invoice-item-mappings.component';
import { InvoiceDefaultConfigurationComponent } from './import/data-import/invoice-import/invoice-default-configuration/invoice-default-configuration.component';
import { InvoiceImportItemsComponent } from './import/data-import/invoice-import/invoice-import-items/invoice-import-items.component';


const routes: Routes = [
  // {
  //   path: "chart-of-accounts/account-types", component: AccountTypesComponent, canActivate: [KeycloakGuardService],
  //   data: { roles: ["aw-client-role"] }
  // },
  // {
  //   path: "chart-of-accounts", component: ChartOfAccountsComponent, canActivate: [KeycloakGuardService],
  //   data: { roles: ["aw-client-role"] }
  // },
  // {
  //   path: "chart-of-accounts/general-ledger", component: GeneralLedgerComponent, canActivate: [KeycloakGuardService],
  //   data: { roles: ["aw-client-role"] }
  // },
  {
    path: "manage-tax-rate-set", component: ManageTaxRateSetComponent, canActivate: [KeycloakGuardService],
    data: {roles: [Roles.Client]}
  },
  {
    path: "manage-tax-rate-set/:id", component: ManageTaxRateSetComponent, canActivate: [KeycloakGuardService],
    data: {roles: [Roles.Client]}
  },
  {
    path: "org-general-info", component: OrgGeneralInfoComponent, canActivate: [KeycloakGuardService],
    data: {roles: [Roles.Client]}
  },
  {
    path: "org-payment-info", component: OrgPaymentInfoComponent, canActivate: [KeycloakGuardService],
    data: {roles: [Roles.Client]}
  },
  {
    path: "org-additional-info", component: OrgAdditionalInfoComponent, canActivate: [KeycloakGuardService],
    data: {roles: [Roles.Client]}
  },
  {
    path: "common/org-termination", component: OrgTerminationComponent, canActivate: [KeycloakGuardService],
    data: {roles: [Roles.Client]}
  },
];


@NgModule({
  declarations: [
    // declare pipes
    AccountNumberMaskPipe,
    BooleanPipe,
    PhonePipe,
    SafeHtmlPipe,
    StringReplacePipe,
    TitleCasePipe,
    NegativeCurrencyPipe,
    SsnMaskPipe,
    RouteParamPipe,

    // Main shared components
    ApprovableMenuComponent,
    CalendarComponent,
    ConnectComponent,
    DataTableComponent,
    DataTableRendererComponent,
    TreeDataTableComponent,
    DraftComponent,
    EntityErrorComponent,
    ExcelFileLoadComponent,
    FilePermissionSettingComponent,
    FileExplorerComponent,
    FileLoadComponent,
    GrowlComponent,
    SupportChatComponent,

    // Approval/Workflow shared components
    ApprovalStatusComponent,
    ApprovableStatusComponent,
    SubmittedForComponent,
    WorkflowStatusComponent,
    TxnErrorCodeComponent,

    // Item Catalog shared components
    AddLineItemComponent,
    ItemCatalogAddItemComponent,
    SearchItemComponent,
    TaxInformationComponent,

    // HCM shared components
    LeaveHistoryComponent,

    // Finance shared components
    AchSelectFundingSourceComponent,
    AccountAutoCompleteComponent,
    AccountsByRootAccountComponent,
    PaymentStatusComponent,
    PaymentDialogComponent,

    // Charge code related shared components
    ChargeCodeAutoCompleteComponent,
    DefaultChargeCodeComponent,
    MyChargeCodeDropdownComponent,
    ShowChargeCodeComponent,

    // Org shared components
    ManageTaxRateSetComponent,
    InternalContactsComponent,
    OrgGeneralInfoComponent,
    OrgContactComponent,
    OrgAdditionalInfoComponent,
    OrgPaymentInfoComponent,
    OrgSummaryComponent,
    OrgTerminationComponent,
    OrgAccountSettingComponent,

    // Contract/Project shared components
    CompletionEstimateComponent,
    ClinInfoComponent,
    ContractInfoComponent,
    ContractTreeComponent,
    ModHistoryComponent,
    ProjectInfoComponent,
    ProjectTaskTreeComponent,
    ProjectTaskBalanceComponent,
    ProjectResourceBalanceComponent,
    RevenueBalanceComponent,
    CompletionEstimateComponent,
    AchFaqComponent,
    RateSummaryComponent,
    StringReplacePipe,
    TodoComponent,

    // Utility shared components
    AchFaqComponent,
    InternationalPhoneNumberComponent,
    ClinInfoComponent,
    ContractInfoComponent,
    PdfTemplateDropdownComponent,
    ProjectInfoComponent,
    TemplateComponent,
    TimerComponent,
    VarDirective,
    AbstractDataTableComponent,
    DefaultCaptionComponent,
    DataTableExportComponent,
    AchUpdateCustomerComponent,
    AlternateWorkWeekComponent,
    LegacyInvoiceComponent,
    TransactionDataImportComponent,
    InvoiceImportComponent,
    ExpenseReportImportComponent,
    InvoiceVendorMappingsComponent,
    InvoiceChargeCodeMappingsComponent,
    InvoiceItemMappingsComponent,
    InvoiceDefaultConfigurationComponent,
    InvoiceImportItemsComponent,
  ],
  imports: [
    CommonModule, FormsModule, ReactiveFormsModule,

    DndModule.forRoot(),

    // PrimeNG modules used. Add any module used in the App here and export it
    // only in v5: GrowlModule, PrimeSharedModule
    AccordionModule, AutoCompleteModule, BreadcrumbModule, ButtonModule, CalendarModule, CheckboxModule, ConfirmDialogModule,
    ContextMenuModule, DialogModule, DragDropModule, DropdownModule, FieldsetModule, FileUploadModule, FullCalendarModule,
    InputMaskModule, InputSwitchModule, InputTextareaModule, InputTextModule, InplaceModule, KeyFilterModule, MenuModule, MenubarModule,
    MultiSelectModule, OverlayPanelModule, PanelModule, PanelMenuModule, PasswordModule, PickListModule, RadioButtonModule, RippleModule,
    SelectButtonModule, SpinnerModule, StepsModule, TableModule, TabViewModule, ToastModule, TreeModule, TreeTableModule, ToggleButtonModule,
    ToolbarModule, TooltipModule,

    NgxChartsModule,  // angular chart module used to render simple charts; Qlik is used for advanced charts
    NgxIntlTelInputModule,
    NgxPrintModule,

    CountdownModule,

    RouterModule.forChild(routes), TooltipModule,
  ],
  exports: [
    // Angular Modules
    CommonModule, FormsModule, ReactiveFormsModule,

    // PrimeNG Modules
    // Only in v5: GrowlModule -> ToastModule, ScheduleModule -> FullCalendarModule,
    // PrimeSharedModule??
    AccordionModule, AutoCompleteModule, BreadcrumbModule, ButtonModule, CalendarModule, CheckboxModule, ConfirmDialogModule,
    ContextMenuModule, DialogModule, DragDropModule, DropdownModule, FieldsetModule, FileUploadModule, FullCalendarModule,
    InputMaskModule, InputSwitchModule, InputTextareaModule, InputTextModule, InplaceModule, KeyFilterModule, MenuModule, MenubarModule,
    MultiSelectModule, OverlayPanelModule, PanelModule, PanelMenuModule, PasswordModule, PickListModule, RadioButtonModule, RippleModule,
    SelectButtonModule, SpinnerModule, StepsModule, TableModule, TabViewModule, ToastModule, TreeModule, TreeTableModule, ToggleButtonModule,
    ToolbarModule, TooltipModule,

    NgxChartsModule,
    NgxIntlTelInputModule,
    NgxPrintModule,

    CountdownModule,

    // Shared Pipes
    AccountNumberMaskPipe,
    BooleanPipe,
    PhonePipe,
    SafeHtmlPipe,
    StringReplacePipe,
    TitleCasePipe,
    NegativeCurrencyPipe,
    SsnMaskPipe,


    // Main shared components
    ApprovableMenuComponent,
    CalendarComponent,
    ConnectComponent,
    DataTableComponent,
    DataTableRendererComponent,
    TreeDataTableComponent,
    DraftComponent,
    EntityErrorComponent,
    ExcelFileLoadComponent,
    FilePermissionSettingComponent,
    FileExplorerComponent,
    FileLoadComponent,
    GrowlComponent,
    SupportChatComponent,

    // Approval/Workflow shared components
    ApprovalStatusComponent,
    ApprovableStatusComponent,
    SubmittedForComponent,
    WorkflowStatusComponent,
    TxnErrorCodeComponent,

    // Item Catalog shared components
    AddLineItemComponent,
    ItemCatalogAddItemComponent,
    SearchItemComponent,
    TaxInformationComponent,

    // HCM shared components
    LeaveHistoryComponent,

    // Finance shared components
    AchSelectFundingSourceComponent,
    AccountAutoCompleteComponent,
    AccountsByRootAccountComponent,
    PaymentStatusComponent,
    PaymentDialogComponent,

    // Charge code related shared components
    ChargeCodeAutoCompleteComponent,
    DefaultChargeCodeComponent,
    MyChargeCodeDropdownComponent,
    ShowChargeCodeComponent,

    // Org shared components
    ManageTaxRateSetComponent,
    InternalContactsComponent,
    OrgGeneralInfoComponent,
    OrgContactComponent,
    OrgAdditionalInfoComponent,
    OrgPaymentInfoComponent,
    OrgSummaryComponent,
    OrgAccountSettingComponent,

    // Contract/Project shared components
    CompletionEstimateComponent,
    ClinInfoComponent,
    ContractInfoComponent,
    ContractTreeComponent,
    ModHistoryComponent,
    ProjectInfoComponent,
    ProjectTaskTreeComponent,
    ProjectTaskBalanceComponent,
    ProjectResourceBalanceComponent,
    RevenueBalanceComponent,

    // Utility shared components
    PdfTemplateDropdownComponent,
    TemplateComponent,
    TimerComponent,
    TodoComponent,
    SupportChatComponent,
    EntityErrorComponent, PaymentStatusComponent, AchSelectFundingSourceComponent, PaymentDialogComponent, ProjectTaskBalanceComponent,
    ProjectResourceBalanceComponent, RevenueBalanceComponent, ProjectResourceBalanceComponent, RevenueBalanceComponent, CompletionEstimateComponent,
    AchFaqComponent, RateSummaryComponent, InternationalPhoneNumberComponent, AchUpdateCustomerComponent, AlternateWorkWeekComponent, LegacyInvoiceComponent,
  ],
  providers: [
    // Shared Pipes
    AccountNumberMaskPipe,
    BooleanPipe,
    PhonePipe,
    SafeHtmlPipe,
    TitleCasePipe,
    NegativeCurrencyPipe,
    SsnMaskPipe,

    // Confirmation Dialog
    ConfirmationService,

    // Shared services
    AccountService,
    // AccountTypesService,
    AddressService,
    CalendarService,
    ChargeCodeService,
    CommonDataService,
    CommonDocRepoService,
    CommonTransformationService,
    CommonTreeService,
    DateService,
    DirectoryService,
    FileService,
    HrService,
    ItemCatalogService,
    LocationService,
    MathService,
    PaymentService,
    PayrollService,
    ProcurementService,
    ProjectService,
    RecruitingService,
    RateService,
    PhoneService,
    PrimeNgSupportService,
    RestrictedSolutionManagerService,
    TradingPartnerService,
    UtilityService
  ]
})
export class SharedModule {
}
